<template>
  <v-dialog v-model="show" max-width="600px" dark persistent>
    <v-card dark>
      <v-card-title class="text-h6">
        {{ isEditing ? "Edit Item" : "Add Item" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-form ref="form" v-model="valid">
          <!-- Item Type -->
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                :items="availableItemTypes"
                v-model="localItem.itemType"
                label="Item Type"
                outlined
                dense
                :rules="[(v) => !!v || 'Item type is required']"
                @change="onItemTypeChange"
              >
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Group Type (for Groups) -->
          <!-- Removed: Group itemType automatically sets GroupType to "Standard" -->

          <!-- Group Name (for Groups) -->
          <v-row v-if="localItem.itemType === 'Group'">
            <v-col cols="12">
              <v-text-field
                v-model="localItem.GroupName"
                label="Group Name"
                outlined
                dense
                :rules="[(v) => !!v || 'Group name is required']"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Group Name (for Selections) -->
          <v-row v-if="localItem.itemType === 'Selection Group'">
            <v-col cols="12">
              <v-text-field
                v-model="localItem.GroupName"
                label="Group Name"
                outlined
                dense
                :rules="[(v) => !!v || 'Group name is required']"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Selection Name (for Selection Groups) -->
          <v-row
            v-if="
              localItem.itemType === 'Selection Group' ||
              (localItem.itemType === 'Group' &&
                localItem.GroupType === 'Selection')
            "
          >
            <v-col cols="12">
              <v-text-field
                v-model="localItem.SelectionName"
                label="Selection Name"
                outlined
                dense
                :rules="[(v) => !!v || 'Selection name is required']"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Product Selector (for Products) -->
          <v-row v-if="!isGroup">
            <v-col cols="12">
              <v-autocomplete
                :items="availableProducts"
                v-model="selectedProduct"
                item-text="ProductName"
                item-value="ProductId"
                return-object
                label="Product"
                outlined
                dense
                :rules="[(v) => !!v || 'Product is required']"
                @change="onProductChange"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>
                      <v-icon small color="red lighten-1"
                        >mdi-information</v-icon
                      >
                      No products found
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Colors -->
          <v-row>
            <v-col cols="6">
              <v-autocomplete
                :key="`text-color-${show}`"
                :items="availableColors"
                v-model="localItem.textColour"
                item-text="ColourName"
                return-object
                label="Text Colour"
                outlined
                dense
                :rules="[(v) => !!v || 'Text color is required']"
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    small
                    :style="{
                      color: item.ColourValue,
                      backgroundColor: getContrastColor(item.ColourValue),
                    }"
                  >
                    <v-icon left small :style="{ color: item.ColourValue }"
                      >mdi-circle</v-icon
                    >
                    {{ item.ColourName }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left small :style="{ color: item.ColourValue }"
                        >mdi-circle</v-icon
                      >
                      {{ item.ColourName }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                :key="`background-color-${show}`"
                :items="availableColors"
                v-model="localItem.backgroundColour"
                item-text="ColourName"
                return-object
                label="Background Colour"
                outlined
                dense
                :rules="[(v) => !!v || 'Background color is required']"
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    small
                    :style="{
                      backgroundColor: item.ColourValue,
                      color: getContrastColor(item.ColourValue),
                    }"
                  >
                    <v-icon left small>mdi-circle</v-icon>
                    {{ item.ColourName }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left small :style="{ color: item.ColourValue }"
                        >mdi-circle</v-icon
                      >
                      {{ item.ColourName }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Preview -->
          <v-row v-if="localItem.textColour && localItem.backgroundColour">
            <v-col cols="12">
              <v-card
                class="preview-card pa-2"
                :style="{
                  backgroundColor: localItem.backgroundColour.ColourValue,
                  color: localItem.textColour.ColourValue,
                }"
              >
                <div class="text-center">
                  <span class="font-weight-bold">{{ getDisplayName() }}</span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn v-if="isEditing" color="red darken-1" text @click="deleteItem">
          <v-icon left>mdi-delete</v-icon>
          Delete
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" @click="save" :disabled="!valid">
          {{ isEditing ? "Update" : "Add" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "LayoutItemDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: () => ({}),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    currentLevel: {
      type: Number,
      default: 0,
    },
    isInsideSelectionGroup: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: false,
      localItem: {
        itemType: "Product",
        textColour: null,
        backgroundColour: null,
        GroupName: "",
        GroupType: "",
        SelectionName: "",
        ProductId: null,
      },
    };
  },

  computed: {
    ...mapState("admin", ["availableProducts", "availableColors"]),

    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },

    availableItemTypes() {
      // If we're inside a Selection group, only allow Products
      if (this.isInsideSelectionGroup) {
        return ["Product"];
      }

      // At level 3, only allow products
      if (this.currentLevel >= 3) {
        return ["Product"];
      }
      return ["Group", "Selection Group", "Product"];
    },

    isGroup() {
      return (
        this.localItem.itemType === "Group" ||
        this.localItem.itemType === "Selection Group"
      );
    },

    isSelectionGroup() {
      return (
        this.localItem.itemType === "Selection Group" ||
        (this.isGroup && this.localItem.GroupType === "Selection")
      );
    },

    selectedProduct: {
      get() {
        if (!this.localItem.ProductId?.ProductId) return null;
        return this.availableProducts.find(
          (product) => product.ProductId === this.localItem.ProductId.ProductId
        );
      },
      set(product) {
        if (product) {
          this.localItem.ProductId = { ProductId: product.ProductId };
        } else {
          delete this.localItem.ProductId;
        }
      },
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },

    item: {
      handler(newItem) {
        // Ensure proper initialization of reactive properties
        this.localItem = {
          itemType: "Product",
          textColour: null,
          backgroundColour: null,
          GroupName: "",
          GroupType: "",
          SelectionName: "",
          ProductId: null,
          ...newItem,
        };

        // Rehydrate color objects if they only contain ColourId/ColourValue
        this.rehydrateColors();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    resetForm() {
      // Ensure proper initialization of reactive properties
      this.localItem = {
        itemType: "Product",
        textColour: null,
        backgroundColour: null,
        GroupName: "",
        GroupType: "",
        SelectionName: "",
        ProductId: null,
        ...this.item,
      };

      // Rehydrate color objects if they only contain ColourId/ColourValue
      this.rehydrateColors();

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    rehydrateColors() {
      // Only rehydrate if availableColors is available
      if (!this.availableColors || !Array.isArray(this.availableColors)) {
        return;
      }

      // Rehydrate textColour if it exists but lacks ColourName
      if (this.localItem.textColour && !this.localItem.textColour.ColourName) {
        const fullColor = this.availableColors.find(
          (color) => color.ColourId === this.localItem.textColour.ColourId
        );
        if (fullColor) {
          this.localItem.textColour = fullColor;
        }
      }

      // Rehydrate backgroundColour if it exists but lacks ColourName
      if (
        this.localItem.backgroundColour &&
        !this.localItem.backgroundColour.ColourName
      ) {
        const fullColor = this.availableColors.find(
          (color) => color.ColourId === this.localItem.backgroundColour.ColourId
        );
        if (fullColor) {
          this.localItem.backgroundColour = fullColor;
        }
      }
    },

    onItemTypeChange() {
      // Reset item-specific fields when type changes
      if (this.localItem.itemType === "Group") {
        delete this.localItem.ProductId;
        delete this.localItem.SelectionName;
        // Automatically set GroupType to "Standard" for Group itemType
        this.$set(this.localItem, "GroupType", "Standard");
        if (!this.localItem.GroupName) {
          this.$set(this.localItem, "GroupName", "");
        }
      } else if (this.localItem.itemType === "Selection Group") {
        delete this.localItem.ProductId;
        delete this.localItem.GroupType;
        if (!this.localItem.GroupName) {
          this.$set(this.localItem, "GroupName", "");
        }
        if (!this.localItem.SelectionName) {
          this.$set(this.localItem, "SelectionName", "");
        }
      } else {
        this.$delete(this.localItem, "GroupName");
        this.$delete(this.localItem, "GroupType");
        this.$delete(this.localItem, "SelectionName");
        this.$delete(this.localItem, "Objects");
      }
    },

    onProductChange(product) {
      // This method is called when a product is selected
      if (product) {
        this.localItem.ProductId = { ProductId: product.ProductId };
      } else {
        delete this.localItem.ProductId;
      }
    },

    getDisplayName() {
      if (this.isGroup) {
        return this.localItem.GroupName || "Group Name";
      } else {
        // Lookup product details from availableProducts
        if (this.localItem.ProductId?.ProductId) {
          const product = this.availableProducts.find(
            (p) => p.ProductId === this.localItem.ProductId.ProductId
          );
          return product?.ShortName || product?.ProductName || "Product Name";
        }
        return "Product Name";
      }
    },

    getContrastColor(hexColor, forBackground = false) {
      // Simple contrast calculation
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      if (forBackground) {
        // For background color, return opposite contrast
        return brightness > 128 ? "#ffffff" : "#000000";
      } else {
        // For text color on background
        return brightness > 128 ? "#000000" : "#ffffff";
      }
    },

    save() {
      if (this.$refs.form.validate()) {
        const cleanItem = this.buildCleanItem();
        this.$emit("save", cleanItem);
        this.close();
      }
    },

    buildCleanItem() {
      // Base required fields for all items
      const cleanItem = {
        itemType:
          this.localItem.itemType === "Selection Group"
            ? "Group"
            : this.localItem.itemType,
        textColour: this.localItem.textColour
          ? {
              ColourId: this.localItem.textColour.ColourId,
              ColourValue: this.localItem.textColour.ColourValue,
            }
          : null,
        backgroundColour: this.localItem.backgroundColour
          ? {
              ColourId: this.localItem.backgroundColour.ColourId,
              ColourValue: this.localItem.backgroundColour.ColourValue,
            }
          : null,
      };

      // Add level and position if they exist (these might be set by parent)
      if (this.localItem.level !== undefined) {
        cleanItem.level = this.localItem.level;
      }
      if (this.localItem.position !== undefined) {
        cleanItem.position = this.localItem.position;
      }

      // Add fields specific to itemType
      if (this.localItem.itemType === "Product") {
        if (this.localItem.ProductId) {
          cleanItem.ProductId = this.localItem.ProductId;
        }
      } else if (this.localItem.itemType === "Group") {
        cleanItem.GroupType = this.localItem.GroupType;

        if (this.localItem.GroupType === "Standard") {
          cleanItem.GroupName = this.localItem.GroupName;
        } else if (this.localItem.GroupType === "Selection") {
          cleanItem.SelectionName = this.localItem.SelectionName;
        }

        // Include Objects array if it exists (for nested items)
        if (this.localItem.Objects) {
          cleanItem.Objects = this.localItem.Objects;
        }
      } else if (this.localItem.itemType === "Selection Group") {
        // Convert Selection itemType to Group with Selection GroupType
        cleanItem.GroupType = "Selection";
        cleanItem.GroupName = this.localItem.GroupName;
        cleanItem.SelectionName = this.localItem.SelectionName;

        // Include Objects array if it exists (for nested items)
        if (this.localItem.Objects) {
          cleanItem.Objects = this.localItem.Objects;
        }
      }

      return cleanItem;
    },

    deleteItem() {
      this.$emit("delete");
      this.close();
    },

    close() {
      this.show = false;
    },
  },
};
</script>

<style scoped>
.preview-card {
  border: 2px solid #333;
  border-radius: 8px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
